import * as React from 'react';
import {
    Dialog,
    DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import {
    Loader2,
    Info,
    ChevronLeft,
    ArrowRight,
    History,
    CheckCircle2,
    Star,
    MessageSquare,
    Zap,
    Layout
} from 'lucide-react';
import { useTenant } from '@/hooks';
import {
    useGetClientTemplateQuery,
    useEvaluatePeersMutation,
    useEvaluateExternalPeersMutation,
} from '@/state';
import { toast } from 'sonner';
import { cn, getInitials } from '@/lib/utils';
import type { EvaluatePeerRequest, EvaluationModalProps } from '@/types';
import NoData from '@/components/no-data';

const RATING_LABELS = [
    { value: 1, label: 'Novice' },
    { value: 2, label: 'Advanced Beginner' },
    { value: 3, label: 'Competent' },
    { value: 4, label: 'Proficient' },
    { value: 5, label: 'Expert' },
];

// Color mapping for skill levels
const LEVEL_COLORS: Record<number, { bg: string; border: string; text: string; hover: string; icon: string }> = {
    1: { bg: 'red-500/10', border: 'red-500', text: 'red-700 dark:text-red-300', hover: 'hover:border-red-500/50 hover:bg-red-500/5 hover:shadow-red-500/5', icon: 'bg-red-500 text-white' },
    2: { bg: 'orange-500/10', border: 'orange-500', text: 'orange-700 dark:text-orange-300', hover: 'hover:border-orange-500/50 hover:bg-orange-500/5 hover:shadow-orange-500/5', icon: 'bg-orange-500 text-white' },
    3: { bg: 'blue-500/10', border: 'blue-500', text: 'blue-700 dark:text-blue-300', hover: 'hover:border-blue-500/50 hover:bg-blue-500/5 hover:shadow-blue-500/5', icon: 'bg-blue-500 text-white' },
    4: { bg: 'emerald-500/10', border: 'emerald-500', text: 'emerald-700 dark:text-emerald-300', hover: 'hover:border-emerald-500/50 hover:bg-emerald-500/5 hover:shadow-emerald-500/5', icon: 'bg-emerald-500 text-white' },
    5: { bg: 'purple-500/10', border: 'purple-500', text: 'purple-700 dark:text-purple-300', hover: 'hover:border-purple-500/50 hover:bg-purple-500/5 hover:shadow-purple-500/5', icon: 'bg-purple-500 text-white' },
};

const DREYFUS_LEVELS = [
    {
        level: 1,
        label: 'Novice',
        knowledge:
            "Minimal, or 'textbook' knowledge without connecting it to practice",
        standard: 'Unlikely to be satisfactory unless closely supervised',
        autonomy: 'Needs close supervision or instruction',
        complexity: 'Little or no conception of dealing with complexity',
        context: 'Tends to see actions in isolation',
    },
    {
        level: 2,
        label: 'Advanced Beginner',
        knowledge: 'Working knowledge of key aspects of practice',
        standard:
            'Straightforward tasks likely to be completed to an acceptable standard',
        autonomy:
            'Able to achieve some steps using own judgement, but supervision needed for overall task',
        complexity:
            'Appreciates complex situations but only able to achieve partial resolution',
        context: 'Sees actions as a series of steps',
    },
    {
        level: 3,
        label: 'Competent',
        knowledge: 'Good working and background knowledge of area of practice',
        standard: 'Fit for purpose, though may lack refinement',
        autonomy: 'Able to achieve most tasks using own judgement',
        complexity:
            'Copes with complex situations through deliberate analysis and planning',
        context: 'Sees actions at least partly in terms of longer-term goals',
    },
    {
        level: 4,
        label: 'Proficient',
        knowledge: 'Depth of understanding of discipline and area of practice',
        standard: 'Fully acceptable standard achieved routinely',
        autonomy:
            'Able to take full responsibility for own work (and that of others where applicable)',
        complexity:
            'Deals with complex situations holistically, decision-making more confident',
        context: "Sees overall 'picture' and how individual actions fit within it",
    },
    {
        level: 5,
        label: 'Expert',
        knowledge:
            'Authoritative knowledge of discipline and deep tacit understanding across area of practice',
        standard: 'Excellence achieved with relative ease',
        autonomy:
            'Able to take responsibility for going beyond existing standards and creating own interpretations',
        complexity:
            'Holistic grasp of complex situations, moves between intuitive and analytical approaches with ease',
        context:
            "Sees overall 'picture' and alternative approaches; vision of what may be possible",
    },
];

const buildLevelTooltip = (level: (typeof DREYFUS_LEVELS)[number]) => (
    <div className="space-y-2 text-xs">
        <p className="font-semibold">
            {level.level}. {level.label}
        </p>
        <div className="space-y-1 text-muted-foreground">
            <strong>Knowledge</strong>
            <p>{level.knowledge}</p>
            <strong>Standard of work</strong>
            <p>{level.standard}</p>
            <strong>Autonomy</strong>
            <p>{level.autonomy}</p>
            <strong>Coping with complexity</strong>
            <p>{level.complexity}</p>
            <strong>Perception of context</strong>
            <p>{level.context}</p>
        </div>
    </div>
);

export const EvaluationModal = ({
    open,
    onOpenChange,
    projectId,
    members = [],
    skills = [],
    isExternal = false,
}: EvaluationModalProps & { isExternal?: boolean }) => {
    const { tenant } = useTenant();
    const { data: template } = useGetClientTemplateQuery(
        { tenant: tenant! },
        { skip: !tenant || !open }
    );
    const [evaluatePeers, { isLoading: isSubmitting }] =
        useEvaluatePeersMutation();
    const [evaluateExternalPeers, { isLoading: isExternalSubmitting }] =
        useEvaluateExternalPeersMutation();

    const [ratings, setRatings] = React.useState<Record<string, number>>({});
    const [feedbacks, setFeedbacks] = React.useState<Record<string, string>>({});

    // Wizard State
    const [currentSkillIndex, setCurrentSkillIndex] = React.useState(0);
    const [currentUserIndex, setCurrentUserIndex] = React.useState(0);
    const [isSummaryView, setIsSummaryView] = React.useState(false);
    const [previousUserIndex, setPreviousUserIndex] = React.useState(0);

    // Modes & Multi-pass Flow
    const [evaluationMode, setEvaluationMode] = React.useState<'CURRENT' | 'SHOW_SKILL'>('CURRENT');
    const [showModeSelection, setShowModeSelection] = React.useState(true);
    const [isCommentPhase, setIsCommentPhase] = React.useState(false);
    const [showCommentPrompt, setShowCommentPrompt] = React.useState(false);

    // Filter members to only include students for peer evaluation
    const evaluationMembers = React.useMemo(() => {
        return members.filter(
            (m) =>
                m.candidate_email !== template?.email &&
                String(m.role_display || '').toUpperCase() === 'STUDENT'
        );
    }, [members, template?.email]);

    // Reset UI state when modal opens to ensure user is asked for mode each time
    React.useEffect(() => {
        if (open) {
            setShowModeSelection(true);
            setIsCommentPhase(false);
            setShowCommentPrompt(false);
            setIsSummaryView(false);
        }
    }, [open]);

    const handleRatingChange = (userId: string, skillId: number, rating: number) => {
        setRatings((prev) => ({ ...prev, [`${userId}-${skillId}`]: rating }));

        if (evaluationMode !== 'SHOW_SKILL' || isCommentPhase || isSummaryView) {
            return;
        }

        const isLastSkill = currentSkillIndex >= skills.length - 1;
        const isLastUser = currentUserIndex >= evaluationMembers.length - 1;

        if (!isLastSkill) {
            setCurrentSkillIndex((prev) => prev + 1);
            return;
        }

        if (!isLastUser) {
            setCurrentUserIndex((prev) => prev + 1);
            setCurrentSkillIndex(0);
            return;
        }

        setShowCommentPrompt(true);
    };

    const handleFeedbackChange = (userId: string, skillId: number, feedback: string) => {
        setFeedbacks((prev) => ({ ...prev, [`${userId}-${skillId}`]: feedback }));
    };

    const currentSkill = skills[currentSkillIndex];
    const currentUser = evaluationMembers[currentUserIndex];
    const currentSkillId = currentSkill?.skill_id || currentSkill?.skill || currentSkill?.id || 0;
    const compositeKey = currentUser ? `${currentUser.candidate}-${currentSkillId}` : '';

    const [direction, setDirection] = React.useState<'forward' | 'backward'>('forward');
    const [lastSaved, setLastSaved] = React.useState<string>(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));

    // Track user changes to update direction for animations
    React.useEffect(() => {
        if (currentUserIndex !== previousUserIndex) {
            setPreviousUserIndex(currentUserIndex);
        }
    }, [currentUserIndex, previousUserIndex]);

    const handleSaveDraft = async () => {
        setLastSaved(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
        toast.info('Draft saved successfully');
    };

    const handleClearDraft = () => {
        setRatings({});
        setFeedbacks({});
        setCurrentUserIndex(0);
        setCurrentSkillIndex(0);
        setIsSummaryView(false);
        setShowModeSelection(true);
        setIsCommentPhase(false);
        setShowCommentPrompt(false);
        setDirection('backward');
        toast.info('Draft cleared');
    };

    const handleNext = () => {
        if (currentSkillIndex < skills.length - 1) {
            setCurrentSkillIndex(prev => prev + 1);
        } else if (currentUserIndex < evaluationMembers.length - 1) {
            setDirection('forward');
            setCurrentUserIndex(prev => prev + 1);
            setCurrentSkillIndex(0);
        } else {
            setDirection('forward');
            if (evaluationMode === 'SHOW_SKILL' && !isCommentPhase) {
                setShowCommentPrompt(true);
            } else {
                setIsSummaryView(true);
            }
        }
    };

    const handleStartComments = () => {
        setIsCommentPhase(true);
        setShowCommentPrompt(false);
        setCurrentUserIndex(0);
        setCurrentSkillIndex(0);
        setDirection('forward');
    };

    const handleSkipComments = () => {
        setShowCommentPrompt(false);
        setIsSummaryView(true);
    };

    const handleBack = () => {
        if (isSummaryView) {
            setIsSummaryView(false);
            return;
        }
        if (currentSkillIndex > 0) {
            setCurrentSkillIndex(prev => prev - 1);
        } else if (currentUserIndex > 0) {
            setDirection('backward');
            setCurrentUserIndex(prev => prev - 1);
            setCurrentSkillIndex(skills.length - 1);
        }
    };

    const handleSubmit = async () => {
        if (!tenant) {
            toast.error('Tenant information missing');
            return;
        }

        const schema = tenant || 'Cogknit3';
        const payload: EvaluatePeerRequest[] = evaluationMembers.flatMap((member) =>
            skills.map((skill) => {
                const skillId = skill.skill_id || skill.skill || skill.id || 0;
                const key = `${member.candidate}-${skillId}`;
                return {
                    skill_name: skill.skill_name || skill.name || '',
                    description: skill.skill_description || skill.description || skill.skill_name || skill.name || '',
                    evaluation_points: ratings[key] || 3,
                    evaluation_schema: schema,
                    evaluator_schema: schema,
                    feedback: feedbacks[key] || '',
                    project: projectId,
                    user_id: member.candidate,
                    user_skills: skillId,
                };
            })
        );

        try {
            if (isExternal) {
                await evaluateExternalPeers({ data: payload }).unwrap();
            } else {
                await evaluatePeers({ tenant: tenant, data: payload }).unwrap();
            }
            toast.success('Evaluations submitted successfully');
            onOpenChange(false);
            setCurrentSkillIndex(0);
            setCurrentUserIndex(0);
            setIsSummaryView(false);
        } catch (error) {
            toast.error('Failed to submit evaluations.');
        }
    };

    const totalSteps = skills.length * evaluationMembers.length;
    const currentStepNumber = (currentUserIndex * skills.length) + currentSkillIndex + 1;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[100vw] w-full h-[100dvh] sm:h-[95vh] sm:max-w-[1400px] p-0 border-0 bg-background text-foreground overflow-hidden shadow-2xl flex flex-row transition-colors duration-300">

                {/* Sidebar - Members List */}
                <div className="hidden lg:flex w-[280px] border-r border-border bg-muted/20 flex-col p-6 space-y-8">
                    <div className="space-y-1">
                        <h2 className="text-xl font-bold tracking-tight">Students</h2>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-widest">Step by step evaluation</p>
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                        {evaluationMembers.length === 0 ? (
                            <NoData title='No Users Found' />
                        ) : (
                            evaluationMembers.map((member, idx) => {
                                const isActive = idx === currentUserIndex && !isSummaryView;
                                const isCompleted = skills.every(s => (ratings[`${member.candidate}-${s.skill_id || s.skill || s.id || 0}`] || 0) > 0);

                                // A user is reachable if they are the current user, or if all previous users are completed
                                const isReachable = idx === 0 || evaluationMembers.slice(0, idx).every(m =>
                                    skills.every(s => (ratings[`${m.candidate}-${s.skill_id || s.skill || s.id || 0}`] || 0) > 0)
                                );

                                return (
                                    <button
                                        key={member.candidate}
                                        disabled={!isReachable}
                                        onClick={() => {
                                            if (isReachable) {
                                                if (idx > currentUserIndex) {
                                                    setDirection('forward');
                                                } else if (idx < currentUserIndex) {
                                                    setDirection('backward');
                                                }
                                                setCurrentUserIndex(idx);
                                                setCurrentSkillIndex(0);
                                                setIsSummaryView(false);
                                            }
                                        }}
                                        className={cn(
                                            "w-full flex items-center gap-3 p-3 rounded-2xl transition-all duration-200 group text-left",
                                            isActive
                                                ? "bg-primary/10 ring-1 ring-primary/30"
                                                : isReachable ? "hover:bg-accent/50" : "opacity-40 cursor-not-allowed"
                                        )}
                                    >
                                        <div className="relative">
                                            <Avatar className="h-10 w-10 ring-2 ring-transparent group-hover:ring-primary/20 transition-all shadow-sm">
                                                {member.photo_url && (
                                                    <AvatarImage src={member.photo_url} alt={member.candidate_first_name} />
                                                )}
                                                <AvatarFallback className="bg-muted text-muted-foreground text-xs font-bold">
                                                    {getInitials(member.candidate_first_name, member.candidate_last_name)}
                                                </AvatarFallback>
                                            </Avatar>
                                            {isCompleted && (
                                                <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full h-4 w-4 flex items-center justify-center border-2 border-background">
                                                    <CheckCircle2 className="h-2.5 w-2.5 text-white" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={cn("text-sm font-bold truncate", isActive ? "text-primary" : "text-foreground")}>
                                                {member.candidate_first_name}
                                            </p>
                                            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-tighter">
                                                {isCompleted ? "Completed" : isActive ? "Evaluating now" : isReachable ? "Available" : "Locked"}
                                            </p>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Main Content Pane */}
                <div className="flex-1 flex flex-col min-w-0 bg-background">

                    {/* Compact Wizard Header */}
                    <div className="relative min-h-16 px-4 sm:px-6 py-3 border-b border-border flex items-start justify-between gap-4 bg-background/80 backdrop-blur-md sticky top-0 z-[60]">
                        <div className="flex items-center gap-4 shrink-0">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleBack}
                                disabled={currentUserIndex === 0 && currentSkillIndex === 0 && !isSummaryView}
                                className="text-muted-foreground hover:text-foreground hover:bg-transparent px-0 font-bold"
                                >
                                    <ChevronLeft className="h-4 w-4 mr-0.5" />
                                    <span className="text-xs">Back</span>
                                </Button>
                        </div>

                        <div className="flex flex-col items-end gap-2 shrink-0">
                            <a
                                href="https://devmts.org.uk/dreyfus.pdf"
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-2 text-[10px] font-medium text-muted-foreground text-right transition hover:text-primary"
                                aria-label="Open Dreyfus reference"
                                title="Stan Lester Developments 2005-2021. Dreyfus Model."
                            >
                                <span className="hidden sm:inline">© Stan Lester Developments 2005-2021. Dreyfus Model.</span>
                                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border/60 bg-background/80 text-muted-foreground shadow-sm transition hover:border-primary/40 hover:text-primary dark:bg-slate-950/80 sm:hidden">
                                    <Info className="h-4 w-4" />
                                </span>
                            </a>
                            <div className="flex flex-wrap items-center justify-end gap-2">
                                <Badge variant="secondary" className="bg-green-500/10 text-green-600 border-0 flex items-center gap-1.5 py-1 px-3">
                                    <CheckCircle2 className="h-3 w-3" />
                                    <span className="text-[10px] font-black">Saved {lastSaved}</span>
                                </Badge>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleSaveDraft}
                                    className="bg-background border-border text-xs font-bold hidden sm:flex"
                                >
                                    Save Draft
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleClearDraft}
                                    className="text-muted-foreground hover:text-red-500 hover:bg-red-500/10 text-xs font-bold hidden sm:flex"
                                >
                                    Clear Draft
                                </Button>
                                {totalSteps > 0 && (
                                    <div className="px-3 py-1.5 bg-muted rounded-lg border border-border hidden md:block">
                                        <span className="text-[10px] font-black tracking-widest text-muted-foreground uppercase">STEP {currentStepNumber} OF {totalSteps}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto min-h-0 relative custom-scrollbar flex flex-col">

                        {/* Progress Bar under header */}
                        {totalSteps > 0 && (
                            <div className="w-full h-0.5 bg-muted sticky top-0 z-50">
                                <div
                                    className="h-full bg-primary transition-all duration-700 ease-in-out"
                                    style={{ width: `${(currentStepNumber / totalSteps) * 100}%` }}
                                />
                            </div>
                        )}

                        {evaluationMembers.length === 0 ? (
                            <NoData title='No Users Found' subTitle='There are no students available for evaluation in this project at the moment. Please check back later or contact your instructor.' />

                        ) : showModeSelection ? (
                            <div className="max-w-4xl mx-auto w-full p-8 md:p-12 space-y-12 flex-1 flex flex-col justify-center animate-in fade-in zoom-in duration-500">
                                <div className="text-center space-y-4">
                                    <div className="h-16 w-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                        <Layout className="h-8 w-8 text-primary" />
                                    </div>
                                    <h1 className="text-3xl md:text-4xl font-black tracking-tight text-foreground uppercase">Choose Evaluation Mode</h1>
                                    <p className="text-muted-foreground font-medium max-w-xl mx-auto">Select how you want to proceed with the skill evaluation for this project.</p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-12">
                                    <button
                                        onClick={() => {
                                            setEvaluationMode('CURRENT');
                                            setShowModeSelection(false);
                                        }}
                                        className="group relative flex flex-col p-8 rounded-[2.5rem] border-2 border-border bg-card hover:border-primary/50 hover:bg-primary/5 transition-all duration-500 text-left overflow-hidden shadow-sm hover:shadow-xl"
                                    >
                                        <div className="h-12 w-12 rounded-2xl bg-muted group-hover:bg-primary group-hover:text-primary-foreground flex items-center justify-center mb-6 transition-all duration-500 shadow-inner">
                                            <Zap className="h-6 w-6" />
                                        </div>
                                        <h3 className="text-xl font-black mb-2 uppercase tracking-tight group-hover:text-primary transition-colors">Comprehensive Mode</h3>
                                        <p className="text-sm text-muted-foreground leading-relaxed">Evaluate ratings and provide detailed feedback for each skill in a single pass. Best for deep assessments.</p>
                                        <div className="absolute top-6 right-8 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                            <ArrowRight className="h-5 w-5 text-primary" />
                                        </div>
                                    </button>

                                    <button
                                        onClick={() => {
                                            setEvaluationMode('SHOW_SKILL');
                                            setShowModeSelection(false);
                                        }}
                                        className="group relative flex flex-col p-8 rounded-[2.5rem] border-2 border-border bg-card hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-all duration-500 text-left overflow-hidden shadow-sm hover:shadow-xl"
                                    >
                                        <div className="h-12 w-12 rounded-2xl bg-muted group-hover:bg-emerald-500 group-hover:text-white flex items-center justify-center mb-6 transition-all duration-500 shadow-inner">
                                            <Star className="h-6 w-6" />
                                        </div>
                                        <h3 className="text-xl font-black mb-2 uppercase tracking-tight group-hover:text-emerald-600 transition-colors">Rapid Rating Mode</h3>
                                        <p className="text-sm text-muted-foreground leading-relaxed">Focus on ratings (stars) first for all skills. Add optional comments after all ratings are complete. Best for quick evaluations.</p>
                                        <div className="absolute top-6 right-8 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                            <ArrowRight className="h-5 w-5 text-emerald-500" />
                                        </div>
                                    </button>
                                </div>
                            </div>
                        ) : showCommentPrompt ? (
                            <div className="max-w-4xl mx-auto w-full p-8 md:p-12 space-y-12 flex-1 flex flex-col justify-center animate-in fade-in zoom-in duration-500 text-center">
                                <div className="h-20 w-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto ring-8 ring-emerald-500/5 shadow-inner">
                                    <Star className="h-10 w-10 text-emerald-500" />
                                </div>
                                <div className="space-y-4">
                                    <h1 className="text-3xl md:text-4xl font-black tracking-tight text-foreground uppercase">Ratings Complete!</h1>
                                    <p className="text-muted-foreground font-medium max-w-xl mx-auto leading-relaxed text-lg">
                                        You&apos;ve finished rating all skills. Would you like to provide detailed comments or reviews for any of them before finalizing?
                                    </p>
                                </div>

                                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
                                    <Button
                                        size="lg"
                                        onClick={handleStartComments}
                                        className="h-16 px-10 rounded-full text-lg font-black uppercase tracking-widest bg-emerald-500 hover:bg-emerald-600 shadow-xl shadow-emerald-500/20"
                                    >
                                        <MessageSquare className="mr-3 h-6 w-6" />
                                        Yes, Add Comments
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="lg"
                                        onClick={handleSkipComments}
                                        className="h-16 px-10 rounded-full text-lg font-black uppercase tracking-widest border-2 hover:bg-muted"
                                    >
                                        No, Finalize Now
                                    </Button>
                                </div>
                            </div>
                        ) : !isSummaryView ? (
                            <div
                                key={`${currentUserIndex}-${currentSkillIndex}`}
                                className={cn(
                                    "max-w-4xl mx-auto w-full p-6 sm:p-12 space-y-10 animate-in fade-in duration-500 ease-in-out",
                                    currentUserIndex !== previousUserIndex && (direction === 'forward' ? "slide-in-from-right" : "slide-in-from-left")
                                )}
                            >
                                <div className="space-y-5 text-center">
                                    <div className="inline-flex items-center gap-3 rounded-full border border-border/60 bg-background/80 px-4 py-2 shadow-sm backdrop-blur-sm dark:border-slate-700/70 dark:bg-slate-950/70">
                                        <Avatar className="h-8 w-8 ring-1 ring-primary/10">
                                            {currentUser?.photo_url && (
                                                <AvatarImage src={currentUser.photo_url} alt={currentUser?.candidate_first_name || 'Student'} />
                                            )}
                                            <AvatarFallback className="bg-primary/10 text-[10px] font-bold text-primary">
                                                {getInitials(currentUser?.candidate_first_name || '', currentUser?.candidate_last_name || '')}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="flex flex-col items-start leading-none">
                                            <span className="text-[9px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                                                Currently evaluating
                                            </span>
                                            <span className="text-sm font-bold text-foreground">
                                                {currentUser?.candidate_first_name || 'Student'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <h1 className="text-2xl sm:text-3xl font-black text-foreground tracking-tight leading-none uppercase">
                                            {currentSkill?.skill_name || currentSkill?.name}
                                        </h1>
                                        <p className="text-sm text-muted-foreground max-w-2xl mx-auto font-medium leading-relaxed">
                                            {currentSkill?.skill_description || currentSkill?.description}
                                        </p>
                                    </div>
                                </div>

                                {/* Card Based Rating System */}
                                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 sm:gap-4 py-2">
                                    {RATING_LABELS.map((rating) => {
                                        const levelMeta = DREYFUS_LEVELS[rating.value - 1] ?? DREYFUS_LEVELS[0];
                                        const isActive = (ratings[compositeKey] || 0) === rating.value;
                                        const levelColor = LEVEL_COLORS[rating.value];

                                        return (
                                            <div
                                                key={rating.value}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => handleRatingChange(currentUser.candidate, currentSkillId, rating.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        handleRatingChange(currentUser.candidate, currentSkillId, rating.value);
                                                    }
                                                }}
                                                className={cn(
                                                    "relative flex flex-col items-center justify-center p-6 rounded-3xl border-2 transition-all duration-300 ease-out group overflow-hidden h-[110px] sm:h-[150px] will-change-transform",
                                                    isActive
                                                        ? `bg-${levelColor.bg} border-${levelColor.border} shadow-sm shadow-${levelColor.border}/10 scale-[1.03] z-10`
                                                        : `bg-card border-border ${levelColor.hover} hover:shadow-sm hover:-translate-y-0.5`
                                                )}
                                            >
                                                <div className={cn(
                                                    "w-10 h-10 rounded-full flex items-center justify-center mb-3 transition-all duration-300 ease-out",
                                                    isActive ? levelColor.icon : `bg-muted text-muted-foreground group-hover:${levelColor.icon}`
                                                )}>
                                                    {isActive ? (
                                                        <CheckCircle2 className="h-5 w-5" />
                                                    ) : (
                                                        <span className="text-sm font-black">{rating.value}</span>
                                                    )}
                                                </div>
                                                <span className={cn(
                                                    "text-[10px] font-black uppercase tracking-widest text-center transition-colors duration-300 ease-out",
                                                    isActive ? levelColor.text : `text-muted-foreground group-hover:${levelColor.text}`
                                                )}>
                                                    {rating.label}
                                                </span>
                                                <div className="absolute top-3 right-3 opacity-20 group-hover:opacity-100 transition-opacity">
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <button
                                                                type="button"
                                                                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-border/60 bg-background/70 text-muted-foreground shadow-sm transition hover:border-primary/40 hover:text-primary dark:bg-slate-950/70"
                                                                aria-label={`Learn more about ${rating.label}`}
                                                            >
                                                                <Info className="h-3 w-3" />
                                                            </button>
                                                        </TooltipTrigger>
                                                        <TooltipContent
                                                            className="max-w-xs border-border/60 bg-popover p-3 text-popover-foreground shadow-xl"
                                                            side="top"
                                                        >
                                                            {buildLevelTooltip(levelMeta)}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Evidence & Notes */}
                                {(evaluationMode === 'CURRENT' || isCommentPhase) && (
                                    <div className="space-y-4 pt-6 border-t border-border animate-in fade-in slide-in-from-bottom-4 duration-700">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2 text-foreground/80">
                                                <History className="h-4 w-4 text-primary" />
                                                <h3 className="text-xs font-bold uppercase tracking-widest">Evidence & Notes (Optional)</h3>
                                            </div>

                                        </div>
                                        <div className="bg-card rounded-3xl border border-border overflow-hidden focus-within:ring-1 focus-within:ring-primary/20 transition-all shadow-sm">
                                            <Textarea
                                                placeholder={`Describe observations for ${currentUser?.candidate_first_name}...`}
                                                className="min-h-[160px] bg-transparent border-0 focus-visible:ring-0 rounded-none p-6 text-sm resize-none placeholder:text-muted-foreground/50 leading-relaxed font-medium"
                                                value={feedbacks[compositeKey] || ''}
                                                onChange={(e) =>
                                                    handleFeedbackChange(currentUser.candidate, currentSkillId, e.target.value)
                                                }
                                            />
                                        </div>
                                    </div>
                                )}

                                {evaluationMode === 'SHOW_SKILL' && !isCommentPhase && (
                                    <div className="p-8 rounded-[2.5rem] bg-emerald-500/5 border border-emerald-500/10 text-center space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-700">
                                        <p className="text-emerald-700 font-bold uppercase tracking-widest text-[10px]">Rapid Rating Mode Active</p>
                                        <p className="text-sm text-emerald-600/80 font-medium">Comments are hidden. You can add them later after finishing all skill ratings.</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div
                                className={cn(
                                    "max-w-4xl mx-auto w-full p-8 text-center space-y-8 animate-in fade-in duration-500",
                                    direction === 'forward' ? "slide-in-from-right" : "slide-in-from-left"
                                )}
                            >
                                <div className="h-20 w-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto ring-4 ring-primary/5 shadow-inner">
                                    <CheckCircle2 className="h-10 w-10 text-primary" />
                                </div>
                                <div className="space-y-2">
                                    <h1 className="text-3xl font-black tracking-tight text-foreground uppercase">Cycle Complete</h1>
                                    <p className="text-muted-foreground font-medium">Review your evaluations before finalizing the project cycle.</p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left pt-6 max-h-[40vh] overflow-y-auto pr-2 custom-scrollbar">
                                    {evaluationMembers.map(member => (
                                        <div key={member.candidate} className="bg-muted/10 border border-border rounded-2xl p-4 flex items-center gap-4 hover:bg-muted/20 transition-colors">
                                            <Avatar className="h-10 w-10">
                                                <AvatarFallback className="text-xs">{getInitials(member.candidate_first_name, '')}</AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold truncate text-foreground">{member.candidate_first_name}</p>
                                                <div className="flex gap-1 mt-1 opacity-50">
                                                    {skills.map(s => (
                                                        <div key={s.skill_id} className="h-1 w-3 rounded-full bg-primary/40" />
                                                    ))}
                                                </div>
                                            </div>
                                            <Badge className="bg-primary/20 text-primary border-0 text-[10px] font-black tracking-tighter">
                                                READY
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer Actions */}
                    <div className="h-20 px-8 border-t border-border bg-background flex items-center justify-between shadow-[0_-4px_20px_rgba(0,0,0,0.02)]">
                        <Button
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                        >
                            Close
                        </Button>

                        <div className="flex items-center gap-4">
                            {evaluationMembers.length > 0 && (
                                <>
                                    {!isSummaryView && !showModeSelection && !showCommentPrompt && (
                                        <p className={cn(
                                            "hidden sm:block text-[10px] font-bold tracking-widest uppercase",
                                            (ratings[compositeKey] || 0) > 0 ? "text-green-600" : "text-orange-500"
                                        )}>
                                            {(ratings[compositeKey] || 0) > 0 ? "Ready to proceed" : "Awaiting rating"}
                                        </p>
                                    )}
                                    {!showModeSelection && !showCommentPrompt && (
                                        <Button
                                            onClick={isSummaryView ? handleSubmit : handleNext}
                                            disabled={isSubmitting || isExternalSubmitting || (!isSummaryView && !ratings[compositeKey])}
                                        >
                                            {isSubmitting || isExternalSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                                                <>
                                                    <span>{isSummaryView ? (isCommentPhase ? "Finalize & Submit" : "Finalize") : "Continue"}</span>
                                                    {!isSummaryView && <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
